import React, { useRef } from 'react';

/**
 * All users | Leavers tab bar above the Users list (spec §4.1, revised:
 * Leavers moved out of the Role rail so it can't be missed).
 *
 * Reuses the shared `.admin-subtabs` / `.admin-subtab` pattern and the same
 * roving-tabindex keyboard model as RequestsHubPage (arrows wrap, Home/End).
 *
 * Props:
 *   view          'all' | 'leavers'
 *   onChange      (view) => void
 *   leaversCount  number | '?'   '?' when Odoo has never been checked
 *   panelId       id of the element the tabs control
 */
export const VIEW_TABS = [
  { key: 'all', label: 'All users' },
  { key: 'leavers', label: 'Leavers' },
];

export default function UsersViewTabs({ view, onChange, leaversCount, panelId }) {
  const tabRefs = useRef({});

  const onKeyDown = (e) => {
    const i = VIEW_TABS.findIndex((t) => t.key === view);
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % VIEW_TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + VIEW_TABS.length) % VIEW_TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = VIEW_TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    const key = VIEW_TABS[next].key;
    onChange(key);
    tabRefs.current[key]?.focus();
  };

  // A badge for "nothing outstanding" is noise; "?" (unknown) always shows.
  const showBadge = leaversCount === '?' || leaversCount > 0;

  return (
    <div className="admin-subtabs ut-viewtabs" role="tablist" aria-label="User list">
      {VIEW_TABS.map((t) => {
        const active = view === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`ut-view-tab-${t.key}`}
            ref={(el) => { tabRefs.current[t.key] = el; }}
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            className={`admin-subtab ut-viewtab ${t.key === 'leavers' ? 'ut-viewtab--leavers' : ''} ${active ? 'is-active' : ''}`}
            onClick={() => onChange(t.key)}
            onKeyDown={onKeyDown}
          >
            {t.label}
            {t.key === 'leavers' && showBadge && (
              <span className={`ut-viewtab-badge ${leaversCount === '?' ? 'is-unknown' : ''}`}>{leaversCount}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
